import React, { useCallback, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IonButton, IonIcon } from '@ionic/react';
import { 
  arrowForward, 
  arrowBack, 
  close, 
  checkmarkCircle,
  playCircle,
  playSkipForward
} from 'ionicons/icons';
import { useTutorial } from './TutorialProvider';
import CenteredTutorialModal from './CenteredTutorialModal';
import GameDialogue from './game/GameDialogue';
import TutorialClickPointer from './game/TutorialClickPointer';
import TutorialDesignPicker from './game/TutorialDesignPicker';
import TutorialLauncher from './game/TutorialLauncher';
import useAppModalOpen from './game/useAppModalOpen';
import { whenAppIdle } from './appBusy';
import './TutorialOverlay.css';

const TutorialOverlay = () => {
  const {
    isActive,
    isWaitingForConverter,
    getCurrentStepData,
    getTutorialProgress,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
    currentStep,
    tutorialType,
    loadTutorialDesign,
    activeTutorialId
  } = useTutorial();

  const appModalOpen = useAppModalOpen();
  const [pickingDesign, setPickingDesign] = useState(null);
  const [activeGuideRect, setActiveGuideRect] = useState(null);

  // Minimised, not ended: getting the walkthrough off the screen keeps the
  // learner's place rather than throwing it away. Ending it is a separate
  // control, so this never has to double as the way out.
  const [collapsed, setCollapsed] = useState(false);

  // Minimising outlives the run it belongs to otherwise — this component stays
  // mounted between tutorials, so a learner who minimised and quit would come
  // back to a walkthrough that opened already hidden.
  useEffect(() => {
    if (!isActive) setCollapsed(false);
  }, [isActive]);

  const chooseDesign = (design) => {
    setPickingDesign(design.id);
    loadTutorialDesign(design);
  };

  const [gateOpen, setGateOpen] = useState(true);

  // Read at click time rather than captured, so a marker appearing or being
  // dropped mid-step adjusts the target without restarting the count.
  const [markerCount, setMarkerCount] = useState(0);
  const requiredClicksRef = useRef(1);

  const [targetElement, setTargetElement] = useState(null);
  const [targetElements, setTargetElements] = useState([]);
  const [targetPosition, setTargetPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const [targetPositions, setTargetPositions] = useState([]);
  const [forceUpdate, setForceUpdate] = useState(0);
  const overlayRef = useRef();
  const tooltipRef = useRef();
  // Last resolved target, so the watcher below can ignore no-op mutations.
  const lastTargetRef = useRef({ elements: [], positions: [] });

  const stepData = getCurrentStepData();
  const progress = getTutorialProgress();

  // How far the learner has read through this step's dialogue.
  const [reading, setReading] = useState({ pageIndex: 0, finished: false });
  const onReadingChange = useCallback((next) => {
    setReading((prev) =>
      prev.pageIndex === next.pageIndex && prev.finished === next.finished ? prev : next,
    );
  }, []);

  useEffect(() => {
    setReading({ pageIndex: 0, finished: false });
    setMarkerCount(0);
  }, [stepData?.id]);

  // A step can point at different controls as its dialogue pages along — while
  // it lists the three stitch types all three are outlined, and only once it
  // says "click Satin" does the highlight narrow to that one.
  const pageSpec = stepData?.pageTargets?.[
    Math.min(reading.pageIndex, stepData.pageTargets.length - 1)
  ];
  const activeTarget = pageSpec?.target ?? stepData?.target;
  const activeMultipleTargets = pageSpec?.multipleTargets ?? stepData?.multipleTargets;

  // Some steps ask for a click on the canvas, which exists long before it will
  // actually respond — the app has to be in rail-selection or guide-drawing
  // mode first. `readyWhen` names a control that only exists in that mode, and
  // until that appears the step holds back its marker and its prompt rather
  // than inviting a click that would do nothing.
  useEffect(() => {
    const gate = stepData?.readyWhen;

    if (!gate) {
      setGateOpen(true);
      return undefined;
    }

    const check = () => setGateOpen(!!document.querySelector(gate));
    check();

    const poll = setInterval(check, 200);
    return () => clearInterval(poll);
  }, [stepData?.id, stepData?.readyWhen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Some controls only exist while the pointer is over the thing that owns
  // them — the satin pair's buttons live inside a dot that opens on hover. A
  // learner following a marker hasn't hovered anything yet, so the step would
  // point at a control that isn't in the page. Holding the owner hovered for
  // the length of the step puts it on screen the way reaching for it would.
  useEffect(() => {
    const owner = stepData?.revealBy;
    if (!isActive || collapsed || !owner) return undefined;

    const hover = () => {
      const node = document.querySelector(owner);
      if (!node) return;
      node.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
    };

    hover();
    const poll = setInterval(hover, 400);

    return () => {
      clearInterval(poll);
      const node = document.querySelector(owner);
      // Without a matching leave the control would stay open for the rest of
      // the session: the pointer never really entered, so nothing else will
      // ever tell it the pointer has gone.
      if (node) {
        node.dispatchEvent(new MouseEvent('mouseout', { bubbles: true, relatedTarget: document.body }));
      }
    };
  }, [isActive, collapsed, stepData?.id, stepData?.revealBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // A dialog's mask swallows clicks for the whole app, and it lingers through
  // its fade-out — so a step pointing at the canvas is genuinely unclickable for
  // a moment after the learner dismisses one. Steps whose own control lives
  // inside the dialog are the exception: for those the mask is the point.
  const targetInsideModal = !!targetElements[0]?.closest?.('.ant-modal-wrap, ion-modal');

  // A step that wants a click isn't ready while the thing to click is missing —
  // a panel that hasn't opened yet, or one that got closed again. Saying "your
  // turn" over a screen with no such control is how the tutorial ends up
  // pointing at nothing.
  const stepReady =
    gateOpen &&
    (!appModalOpen || targetInsideModal) &&
    (!stepData?.advanceOnTargetClick || targetElements.length > 0);

  requiredClicksRef.current = markerCount || stepData?.requiredClicks || 1;

  // Find and highlight target element
  useEffect(() => {
    if (!isActive || !activeTarget) {
      lastTargetRef.current = { elements: [], positions: [] };
      setTargetElement(null);
      setTargetElements([]);
      return;
    }

    // Forget the previous step's resolution, so a step that reuses the same
    // target as the one before it still commits and re-draws its highlight.
    lastTargetRef.current = { elements: [], positions: [] };

    // The watcher below re-runs on every DOM mutation, so only push state when
    // the resolved target or its box has actually moved.
    const commit = (elements, positions) => {
      const previous = lastTargetRef.current;
      const unchanged =
        previous.elements.length === elements.length &&
        elements.every((element, i) => element === previous.elements[i]) &&
        positions.every((position, i) => {
          const before = previous.positions[i];
          return before
            && Math.abs(before.top - position.top) < 0.5
            && Math.abs(before.left - position.left) < 0.5
            && Math.abs(before.width - position.width) < 0.5
            && Math.abs(before.height - position.height) < 0.5;
        });

      if (unchanged) return;

      lastTargetRef.current = { elements, positions };
      setTargetElements(elements);
      setTargetPositions(positions);
      setTargetElement(elements[0]);
      setTargetPosition(positions[0]);
    };

    const findTarget = () => {
      // Handle multiple targets if specified
      if (activeMultipleTargets && activeMultipleTargets.length > 0) {
        const elements = [];
        const positions = [];
        
        for (const selector of activeMultipleTargets) {
          const element = document.querySelector(selector);
          if (element) {
            elements.push(element);
            const rect = element.getBoundingClientRect();
            positions.push({
              top: rect.top + window.scrollY,
              left: rect.left + window.scrollX,
              width: rect.width,
              height: rect.height
            });
          }
        }
        
        if (elements.length > 0) {
          // Note: No scrolling - highlight elements in their current position
          commit(elements, positions);
          return true;
        }
      } else {
        // Single target logic (existing)
        const element = document.querySelector(activeTarget);
        
        if (element) {
          const rect = element.getBoundingClientRect();
          const position = {
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
            height: rect.height
          };

          // Note: No scrolling - highlight element in its current position
          commit([element], [position]);
          return true;
        }
      }
      return false;
    };

    // Drop the previous step's highlight while we wait. Leaving it up would
    // point the learner at the control they just used.
    if (!findTarget()) {
      lastTargetRef.current = { elements: [], positions: [] };
      setTargetElement(null);
      setTargetElements([]);
    }

    // Keep watching for the whole step rather than giving up after a couple of
    // tries. Plenty of targets only appear once the learner opens a panel, and
    // the export step's Download button arrives a minute after generation
    // starts — a short retry window leaves those steps with no highlight at all.
    // The same watch re-measures a target that moves or resizes underneath us.
    // Coalesced to one measurement per frame: the dialogue's typewriter mutates
    // the DOM every few milliseconds, and each measurement forces a layout.
    let frame = null;
    const scheduleFind = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        findTarget();
      });
    };

    const observer = new MutationObserver(scheduleFind);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'disabled', 'hidden'],
    });

    const poll = setInterval(findTarget, 500);

    return () => {
      observer.disconnect();
      clearInterval(poll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [isActive, activeTarget, activeMultipleTargets, currentStep]);

  // Advance when the learner actually performs the step. A recorded tutorial is
  // a long chain of ordinary clicks, so requiring a named action per step would
  // mean threading tutorial code through every feature component. Matching on
  // the step's own selector keeps that knowledge here.
  useEffect(() => {
    if (!isActive || !stepData?.advanceOnTargetClick || !stepData?.target) return;

    // Where the step highlights a broad area but only a small thing inside it
    // responds — the canvas versus a satin path — count the click that does the
    // work. Counting the container would march the tutorial on after near
    // misses that changed nothing.
    const countOn = stepData.clickTarget || stepData.target;
    let seen = 0;
    let advanced = false;

    const onClick = (event) => {
      if (advanced) return;
      const node = event.target;
      if (!node || typeof node.closest !== 'function') return;
      if (!node.closest(countOn)) return;

      seen += 1;
      if (seen < requiredClicksRef.current) return;

      advanced = true;
      window.setTimeout(nextStep, stepData.advanceDelay ?? 450);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isActive, stepData, currentStep, nextStep]);

  // Some controls only exist in certain app states — the "Clear guides" button,
  // for instance, is absent when nothing auto-generated any guides. Rather than
  // strand the learner on an impossible instruction, move on.
  useEffect(() => {
    if (!isActive || !stepData?.skipIfMissing || !stepData?.target) return undefined;

    let cancelIdleWait = null;

    const deadline = window.setTimeout(() => {
      // A loading screen is reason enough for the control to be absent, so wait
      // out the wait before calling the step impossible.
      cancelIdleWait = whenAppIdle(() => {
        if (!document.querySelector(stepData.target)) {
          console.log('🎯 Tutorial: skipping step, target never appeared:', stepData.target);
          nextStep();
        }
      });
    }, stepData.skipIfMissing);

    return () => {
      window.clearTimeout(deadline);
      if (cancelIdleWait) cancelIdleWait();
    };
  }, [isActive, stepData, currentStep, nextStep]);

  // Handle window resize to reposition tooltip (but not scroll - highlights stay in place)
  useEffect(() => {
    const handleResize = () => {
      // Force re-render to recalculate positions only on resize
      setForceUpdate(prev => prev + 1);
    };

    if (isActive) {
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isActive]);

  // Position tooltip close to target element
  const getTooltipStyle = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth <= 768;
    const isLarge = stepData?.large;
    
    console.log('🎯 TUTORIAL DEBUG - Viewport:', { viewportWidth, viewportHeight, isMobile });
    console.log('🎯 TUTORIAL DEBUG - Step:', { stepId: stepData?.id, isLarge, hasTarget: !!stepData?.target });
    
    // Larger dimensions for large modals - but ensure they fit in viewport
    let tooltipWidth, tooltipHeight;
    if (isLarge && !isMobile) {
      tooltipWidth = Math.min(600, viewportWidth - 80);
      // Reduced height to ensure content isn't cut off
      tooltipHeight = Math.min(350, viewportHeight - 160);
    } else {
      tooltipWidth = Math.min(isMobile ? 320 : 400, viewportWidth - 40);
      // Ensure sufficient height for content but stay within viewport
      // Increased max height for better content display
      tooltipHeight = Math.min(isMobile ? 280 : 350, viewportHeight - 140);
    }
    
    console.log('🎯 TUTORIAL DEBUG - Calculated size:', { tooltipWidth, tooltipHeight });
    
    const margin = 24;
    
    // If no target element, center it using inline styles only
    if (!targetElement || !stepData?.target) {
      const centeredStyle = {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10001,
        width: tooltipWidth,
        maxWidth: 'calc(100vw - 48px)',
        maxHeight: 'calc(100vh - 80px)',
        overflow: 'auto'
      };
      
      console.log('🎯 TUTORIAL DEBUG - Centered style applied (inline only):', centeredStyle);
      return centeredStyle;
    }

    const rect = targetElement.getBoundingClientRect();
    
    let style = {
      position: 'fixed',
      zIndex: 10001,
      width: tooltipWidth,
      maxWidth: 'calc(100vw - 48px)',
      maxHeight: 'calc(100vh - 140px)', // More breathing room
      overflow: 'auto'
    };

    // Check all positioning options and their viability
    const positions = [
      {
        name: 'right',
        canFit: rect.left + rect.width + tooltipWidth + margin <= viewportWidth,
        style: {
          left: rect.left + rect.width + margin,
          top: Math.max(margin, Math.min(rect.top + (rect.height / 2) - (tooltipHeight / 2), viewportHeight - tooltipHeight - margin))
        }
      },
      {
        name: 'left',
        canFit: rect.left - tooltipWidth - margin >= 0,
        style: {
          left: rect.left - tooltipWidth - margin,
          top: Math.max(margin, Math.min(rect.top + (rect.height / 2) - (tooltipHeight / 2), viewportHeight - tooltipHeight - margin))
        }
      },
      {
        name: 'top',
        canFit: rect.top - tooltipHeight - margin >= 0,
        style: {
          // For elements near the bottom, position with extra margin
          top: Math.max(margin, rect.top - tooltipHeight - margin),
          left: Math.max(margin, Math.min(rect.left + (rect.width / 2) - (tooltipWidth / 2), viewportWidth - tooltipWidth - margin))
        }
      },
      {
        name: 'bottom',
        canFit: rect.top + rect.height + tooltipHeight + margin <= viewportHeight,
        style: {
          // Ensure the tooltip doesn't go below viewport bottom
          top: Math.min(rect.top + rect.height + margin, viewportHeight - tooltipHeight - margin),
          left: Math.max(margin, Math.min(rect.left + (rect.width / 2) - (tooltipWidth / 2), viewportWidth - tooltipWidth - margin))
        }
      }
    ];

    // Find the first position that fits, or use the preferred one with constraints
    let selectedPosition = positions.find(pos => pos.canFit);
    
    if (!selectedPosition) {
      // If nothing fits perfectly, choose the best option and constrain it
      // For bottom elements (like AI Toolkit), position modal in upper portion of screen
      if (rect.bottom > viewportHeight - 200) {
        // Element is near the bottom - position modal in upper/middle area
        const topPosition = {
          name: 'top-adjusted',
          canFit: false,
          style: {
            // Position modal with maximum space, accounting for the bottom element
            top: Math.max(margin, Math.min(
              rect.top - tooltipHeight - margin,
              viewportHeight - tooltipHeight - 160 // Extra space for bottom elements
            )),
            left: Math.max(margin, Math.min(rect.left + (rect.width / 2) - (tooltipWidth / 2), viewportWidth - tooltipWidth - margin))
          }
        };
        selectedPosition = topPosition;
      } else if (rect.top > viewportHeight / 2) {
        selectedPosition = positions.find(pos => pos.name === 'top') || positions[0];
      } else {
        selectedPosition = positions[0]; // Default to right
      }
      
      // Apply strict viewport constraints with better fallback
      const constrainedStyle = { ...selectedPosition.style };
      
      // Ensure tooltip stays within viewport bounds with extra top margin
      constrainedStyle.top = Math.max(margin + 20, Math.min(constrainedStyle.top || margin, viewportHeight - tooltipHeight - margin));
      constrainedStyle.left = Math.max(margin, Math.min(constrainedStyle.left || margin, viewportWidth - tooltipWidth - margin));
      
      // If still off-screen, center it as fallback
      if (constrainedStyle.left < margin || constrainedStyle.left + tooltipWidth > viewportWidth - margin ||
          constrainedStyle.top < margin || constrainedStyle.top + tooltipHeight > viewportHeight - margin) {
        constrainedStyle.top = Math.max(margin, (viewportHeight - tooltipHeight) / 2);
        constrainedStyle.left = Math.max(margin, (viewportWidth - tooltipWidth) / 2);
      }
      
      Object.assign(style, constrainedStyle);
    } else {
      // Apply the selected position but still ensure it's within bounds
      const finalStyle = { ...selectedPosition.style };
      finalStyle.top = Math.max(margin, Math.min(finalStyle.top || margin, viewportHeight - tooltipHeight - margin));
      finalStyle.left = Math.max(margin, Math.min(finalStyle.left || margin, viewportWidth - tooltipWidth - margin));
      Object.assign(style, finalStyle);
    }

    // Final safety check - if tooltip would be off-screen, center it
    if (style.left < margin || style.left + tooltipWidth > viewportWidth - margin) {
      style.left = Math.max(margin, (viewportWidth - tooltipWidth) / 2);
    }
    if (style.top < margin || style.top + tooltipHeight > viewportHeight - margin) {
      style.top = Math.max(margin, (viewportHeight - tooltipHeight) / 2);
    }

    return style;
  };

  const handleNext = () => {
    console.log('🎯 TutorialOverlay: handleNext called');
    console.log('🎯 TutorialOverlay: stepData.showComplete:', stepData?.showComplete);
    if (stepData?.showComplete) {
      completeTutorial();
    } else {
      console.log('🎯 TutorialOverlay: Calling nextStep()');
      nextStep();
    }
  };

  // Create clip-path to cut out the highlighted area(s) from the backdrop
  const getBackdropClipPath = () => {
    if ((!targetElement && targetElements.length === 0) || !stepData?.highlight) {
      return 'none';
    }
    
    const margin = 8; // Extra space around the element for the cutout

    // One cutout spanning every highlighted element, so a step that calls out a
    // row of controls doesn't leave all but the first of them dimmed.
    const rects = targetElements.map((element) => element.getBoundingClientRect());
    const left = Math.max(0, Math.min(...rects.map((r) => r.left)) - margin);
    const top = Math.max(0, Math.min(...rects.map((r) => r.top)) - margin);
    const right = Math.min(window.innerWidth, Math.max(...rects.map((r) => r.right)) + margin);
    const bottom = Math.min(window.innerHeight, Math.max(...rects.map((r) => r.bottom)) + margin);
    
    // Create a clip-path that excludes the highlighted area
    return `polygon(
      0% 0%, 
      0% 100%, 
      ${left}px 100%, 
      ${left}px ${top}px, 
      ${right}px ${top}px, 
      ${right}px ${bottom}px, 
      ${left}px ${bottom}px, 
      ${left}px 100%, 
      100% 100%, 
      100% 0%
    )`;
  };

  if (!isActive || !stepData || isWaitingForConverter || stepData.waiting) {
    return null;
  }

  // The embroidery walkthrough is narrated by a mascot instead of a tooltip, so
  // it renders the spotlight only and hands the copy to the dialogue HUD.
  if (tutorialType === 'embroidery') {
    // Hidden: everything that dims, points or explains comes off the screen,
    // but the walkthrough keeps following along so resuming lands on whatever
    // the learner has since got to rather than where they left.
    if (collapsed) {
      return createPortal(
        <TutorialLauncher
          progress={progress}
          onResume={() => setCollapsed(false)}
          onExit={skipTutorial}
        />,
        document.body,
      );
    }

    const spotlight = targetElements[0]?.getBoundingClientRect();

    // Rendered into the body so it escapes the router outlet's stacking
    // context, which would otherwise trap it beneath the app's modal masks.
    return createPortal(
      <>
        {!stepData.noBackdrop && !appModalOpen && (
          <div
            className={`tutorial-backdrop ${spotlight && stepData.highlight ? 'has-highlight' : ''}`}
            style={{ clipPath: stepData.highlight ? getBackdropClipPath() : 'none' }}
          />
        )}

        {stepData.highlight && targetElements.map((element, index) => {
          const rect = element.getBoundingClientRect();
          return (
            <div
              key={index}
              className="tutorial-highlight"
              style={{
                position: 'fixed',
                top: Math.max(0, rect.top),
                left: rect.left,
                width: rect.width,
                height: Math.min(rect.height, window.innerHeight - rect.top - 10),
                zIndex: 10000,
                pointerEvents: 'none',
                overflow: 'visible'
              }}
            />
          );
        })}

        {stepData.picker && (
          <TutorialDesignPicker onSelect={chooseDesign} busyId={pickingDesign} />
        )}

        {/* The marker is an instruction to act, so it waits until the learner
            has actually read to the end of the step — except where the marker
            is what the words are describing, as when a step points at places on
            the design itself. Those say `showPointer: 'immediate'` and put the
            markers up with the text. */}
        {(reading.finished || stepData.showPointer === 'immediate')
          && stepReady && targetElements[0] && stepData.showPointer !== false && (
          <TutorialClickPointer
            stepData={stepData}
            targetElement={targetElements[0]}
            clickSelector={stepData.clickTarget || stepData.target}
            onCountChange={setMarkerCount}
            onActivePointChange={setActiveGuideRect}
          />
        )}

        <GameDialogue
          stepData={stepData}
          progress={progress}
          targetRect={stepData.designPoints && activeGuideRect ? activeGuideRect : (stepData.highlight ? spotlight : null)}
          onNext={nextStep}
          onPrevious={previousStep}
          onCollapse={() => setCollapsed(true)}
          onExit={skipTutorial}
          onComplete={completeTutorial}
          canGoBack={currentStep > 0}
          onReadingChange={onReadingChange}
          tutorialId={activeTutorialId}
          stepReady={stepReady}
          holeClipPath={stepData.highlight ? getBackdropClipPath() : 'none'}
        />
      </>,
      document.body
    );
  }

  // Use centered modal for steps without targets OR with position='center'
  if (!stepData.target || stepData.position === 'center') {
    return (
      <>
        {/* Backdrop with highlight cutout if there's a target (skip if noBackdrop is true) */}
        {!stepData.noBackdrop && (
          stepData.target && targetElements.length > 0 ? (
            <div 
              className="tutorial-backdrop"
              style={{
                clipPath: getBackdropClipPath()
              }}
            />
          ) : (
            <div className="tutorial-backdrop" />
          )
        )}
        
        {/* Pink highlight boxes for centered modals with targets */}
        {stepData.highlight && stepData.target && targetElements.map((element, index) => {
          const rect = element.getBoundingClientRect();
          // Ensure the highlight doesn't get clipped at the bottom
          const adjustedHeight = Math.min(rect.height, window.innerHeight - rect.top - 10);
          const adjustedTop = Math.max(0, rect.top);
          
          return (
            <div
              key={index}
              className="tutorial-highlight"
              style={{
                position: 'fixed',
                top: adjustedTop,
                left: rect.left,
                width: rect.width,
                height: adjustedHeight,
                zIndex: 10000,
                pointerEvents: 'none',
                overflow: 'visible'
              }}
            />
          );
        })}
        
        {/* Centered Modal */}
        <CenteredTutorialModal
          stepData={stepData}
          progress={progress}
          onNext={nextStep}
          onPrevious={previousStep}
          onSkip={skipTutorial}
          onComplete={completeTutorial}
          currentStep={currentStep}
        />
      </>
    );
  }

  return (
    <>
      {/* Backdrop with cutout for highlighted element */}
      <div 
        className={`tutorial-backdrop ${targetElement && stepData?.highlight ? 'has-highlight' : ''}`}
        ref={overlayRef}
        style={{
          clipPath: targetElement && stepData?.highlight ? getBackdropClipPath() : 'none'
        }}
      />
      
      {/* Target highlight */}
      {/* Highlight box(es) around target element(s) */}
      {stepData.highlight && targetElements.map((element, index) => {
        const rect = element.getBoundingClientRect();
        // Ensure the highlight doesn't get clipped at the bottom
        const adjustedHeight = Math.min(rect.height, window.innerHeight - rect.top - 10);
        const adjustedTop = Math.max(0, rect.top);
        
        return (
          <div
            key={index}
            className="tutorial-highlight"
            style={{
              position: 'fixed',
              top: adjustedTop,
              left: rect.left,
              width: rect.width,
              height: adjustedHeight,
              zIndex: 10000,
              pointerEvents: 'none',
              // Ensure the highlight is visible even if parent has overflow hidden
              overflow: 'visible'
            }}
          />
        );
      })}

      {/* Tutorial tooltip */}
      <div 
        className={`tutorial-tooltip ${stepData?.large ? 'large' : ''}`}
        style={getTooltipStyle()}
        ref={(el) => {
          tooltipRef.current = el;
          if (el) {
            const computedStyle = window.getComputedStyle(el);
            const boundingRect = el.getBoundingClientRect();
            
            console.log('🎯 TUTORIAL DEBUG - Tooltip element FULL:', {
              className: el.className,
              computedStyle: {
                position: computedStyle.position,
                top: computedStyle.top,
                left: computedStyle.left,
                transform: computedStyle.transform,
                width: computedStyle.width,
                height: computedStyle.height,
                zIndex: computedStyle.zIndex,
                animation: computedStyle.animation
              },
              boundingRect: {
                x: boundingRect.x,
                y: boundingRect.y,
                width: boundingRect.width,
                height: boundingRect.height,
                top: boundingRect.top,
                left: boundingRect.left,
                right: boundingRect.right,
                bottom: boundingRect.bottom
              },
              inlineStyle: el.style.cssText,
              viewport: {
                centerX: window.innerWidth / 2,
                centerY: window.innerHeight / 2,
                actualCenterX: boundingRect.left + boundingRect.width / 2,
                actualCenterY: boundingRect.top + boundingRect.height / 2
              }
            });
            
            // Check again after animation should complete
            setTimeout(() => {
              const finalComputedStyle = window.getComputedStyle(el);
              const finalBoundingRect = el.getBoundingClientRect();
              
              console.log('🎯 TUTORIAL DEBUG - AFTER ANIMATION:', {
                computedStyle: {
                  position: finalComputedStyle.position,
                  top: finalComputedStyle.top,
                  left: finalComputedStyle.left,
                  transform: finalComputedStyle.transform
                },
                boundingRect: {
                  x: finalBoundingRect.x,
                  y: finalBoundingRect.y,
                  width: finalBoundingRect.width,
                  height: finalBoundingRect.height
                },
                viewport: {
                  centerX: window.innerWidth / 2,
                  centerY: window.innerHeight / 2,
                  actualCenterX: finalBoundingRect.left + finalBoundingRect.width / 2,
                  actualCenterY: finalBoundingRect.top + finalBoundingRect.height / 2,
                  offsetFromCenterX: (finalBoundingRect.left + finalBoundingRect.width / 2) - (window.innerWidth / 2),
                  offsetFromCenterY: (finalBoundingRect.top + finalBoundingRect.height / 2) - (window.innerHeight / 2)
                }
              });
            }, 700); // After 600ms animation + 100ms buffer
          }
        }}
      >
        {/* Progress bar */}
        <div className="tutorial-progress">
          <div className="tutorial-progress-bar">
            <div 
              className="tutorial-progress-fill"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        </div>

        {/* Close button */}
        <button 
          className="tutorial-close"
          onClick={skipTutorial}
          aria-label="Close tutorial"
        >
          <IonIcon icon={close} />
        </button>

        {/* Content */}
        <div className="tutorial-content">
          <h3 className="tutorial-title">
            {stepData.showComplete && <IonIcon icon={checkmarkCircle} className="tutorial-success-icon" />}
            {stepData.title}
          </h3>
          <div className="tutorial-description">
            {stepData.description.split('\n').map((line, index) => {
              // Handle empty lines as spacing
              if (line.trim() === '') {
                return <br key={index} />;
              }
              
              // Process bold text (**text**)
              const processedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                      .replace(/\*(.*?)\*/g, '<em>$1</em>');
              
              return (
                <div key={index} dangerouslySetInnerHTML={{ __html: processedLine }} />
              );
            })}
          </div>


          {/* Action indicator */}
          {stepData.waitForAction && !stepData.autoAdvance && (
            <div className="tutorial-action-indicator">
              <IonIcon icon={playCircle} />
              <span>
                {stepData.actionRequired 
                  ? 'Click the highlighted button to continue' 
                  : 'Complete this action to continue'
                }
              </span>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="tutorial-controls">
          <div className="tutorial-controls-left">
            {currentStep > 0 && (
              <IonButton 
                fill="clear" 
                size="small"
                onClick={previousStep}
              >
                <IonIcon icon={arrowBack} slot="start" />
                Back
              </IonButton>
            )}
          </div>

          <div className="tutorial-controls-right">
            {/* An action-gated step has no Next button, so a target that fails to
                resolve would otherwise strand the learner mid-tutorial. */}
            {!stepData.showNext && !stepData.showComplete && (
              <IonButton fill="clear" size="small" onClick={nextStep}>
                Skip step
                <IonIcon icon={playSkipForward} slot="end" />
              </IonButton>
            )}

            {(stepData.showNext || stepData.showComplete) && (
              <IonButton 
                size="small"
                onClick={handleNext}
                disabled={stepData.waitForAction && !stepData.autoAdvance}
              >
                {stepData.showComplete ? 'Complete' : (stepData.nextText || 'Next')}
                {!stepData.showComplete && <IonIcon icon={arrowForward} slot="end" />}
              </IonButton>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TutorialOverlay;
